import { useState } from 'react';
import { useBadges } from '@/hooks/useStoreSettings';

interface BadgesManagerProps {
  onToast: (msg: string) => void;
}

const BADGE_COLORS = [
  { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200' },
  { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200' },
  { bg: 'bg-teal-50', text: 'text-teal-700', border: 'border-teal-200' },
  { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200' },
  { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-200' },
];

export default function BadgesManager({ onToast }: BadgesManagerProps) {
  const { badges, addBadge, removeBadge, resetBadges } = useBadges();
  const [newBadge, setNewBadge] = useState('');
  const [error, setError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const handleAdd = () => {
    const trimmed = newBadge.trim();
    if (!trimmed) { setError('يرجى إدخال نص الشارة'); return; }
    if (trimmed.length > 20) { setError('الشارة يجب أن تكون 20 حرف كحد أقصى'); return; }
    if (badges.includes(trimmed)) { setError('هذه الشارة موجودة مسبقاً'); return; }
    setError('');
    addBadge(trimmed);
    setNewBadge('');
    onToast(`تم إضافة شارة "${trimmed}"`);
  };

  const handleDelete = (badge: string) => {
    removeBadge(badge);
    setDeleteConfirm(null);
    onToast(`تم حذف شارة "${badge}"`);
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Info */}
      <div className="flex items-start gap-3 bg-amber-50 rounded-xl px-4 py-3 border border-amber-100">
        <i className="ri-information-line text-amber-600 text-base mt-0.5 flex-shrink-0"></i>
        <p className="text-amber-700 text-xs leading-relaxed">
          الشارات تظهر على بطاقات المنتجات في المتجر. يمكنك إضافة شارات مخصصة وتعيينها للمنتجات من قسم المنتجات.
        </p>
      </div>

      {/* Stats */}
      <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3 border border-gray-100">
        <div className="w-9 h-9 flex items-center justify-center bg-white rounded-lg">
          <i className="ri-price-tag-3-line text-[#1a1a1a] text-base"></i>
        </div>
        <div>
          <p className="text-xl font-black text-[#1a1a1a]">{badges.length}</p>
          <p className="text-xs text-gray-400">شارة متاحة</p>
        </div>
        <button
          onClick={() => { resetBadges(); onToast('تم استعادة الشارات الافتراضية'); }}
          className="mr-auto text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1.5 cursor-pointer whitespace-nowrap transition-colors"
        >
          <i className="ri-refresh-line text-sm"></i>
          استعادة الافتراضي
        </button>
      </div>

      {/* Add new badge */}
      <div className="bg-gray-50 rounded-2xl p-4 border border-gray-100">
        <h3 className="text-sm font-bold text-[#1a1a1a] mb-3 flex items-center gap-2">
          <i className="ri-add-circle-line text-[#1a1a1a]"></i>
          إضافة شارة جديدة
        </h3>
        <div className="flex gap-3">
          <input
            type="text"
            value={newBadge}
            onChange={e => { setNewBadge(e.target.value); setError(''); }}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
            placeholder="مثال: وصل حديثاً"
            maxLength={20}
            className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-[#1a1a1a] transition-colors bg-white"
            style={{ fontFamily: "'Cairo', sans-serif" }}
          />
          <button
            onClick={handleAdd}
            className="bg-[#1a1a1a] text-white rounded-xl px-5 py-2.5 text-sm font-bold hover:bg-[#333] transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-add-line"></i>
            إضافة
          </button>
        </div>
        {error && (
          <p className="text-red-500 text-xs mt-2 flex items-center gap-1">
            <i className="ri-error-warning-line"></i>
            {error}
          </p>
        )}
      </div>

      {/* Badges grid */}
      {badges.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-400">
          <div className="w-14 h-14 flex items-center justify-center bg-gray-100 rounded-full mb-3">
            <i className="ri-price-tag-3-line text-2xl"></i>
          </div>
          <p className="text-sm">لا توجد شارات بعد</p>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {badges.map((badge, idx) => {
            const color = BADGE_COLORS[idx % BADGE_COLORS.length];
            return (
              <div
                key={badge}
                className={`group flex items-center gap-2 px-3 py-1.5 rounded-lg border ${color.bg} ${color.border} transition-all`}
              >
                <span className={`text-sm font-bold ${color.text}`}>{badge}</span>
                <button
                  onClick={() => setDeleteConfirm(badge)}
                  className={`w-4 h-4 flex items-center justify-center rounded-full opacity-0 group-hover:opacity-100 hover:bg-black/10 ${color.text} transition-all cursor-pointer`}
                  title="حذف"
                >
                  <i className="ri-close-line text-xs"></i>
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview note */}
      {badges.length > 0 && (
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100">
          <p className="text-xs text-gray-400 mb-2 font-medium">معاينة الشارات على بطاقة المنتج:</p>
          <div className="flex items-center gap-3 bg-white rounded-xl p-3 border border-gray-100">
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-image-line text-gray-300 text-lg"></i>
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-md border ${BADGE_COLORS[0].bg} ${BADGE_COLORS[0].border} ${BADGE_COLORS[0].text}`}>
                  {badges[0]}
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded w-24 mb-1"></div>
              <div className="h-2 bg-gray-100 rounded w-16"></div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" dir="rtl">
          <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-xl mb-4 mx-auto">
              <i className="ri-price-tag-3-line text-red-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-[#1a1a1a] text-center mb-2">حذف الشارة</h3>
            <p className="text-sm text-gray-500 text-center mb-6">
              هل أنت متأكد من حذف شارة <span className="font-black text-[#1a1a1a]">"{deleteConfirm}"</span>؟
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 border border-gray-200 rounded-lg py-2.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
              >
                إلغاء
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 bg-red-500 text-white rounded-lg py-2.5 text-sm font-medium hover:bg-red-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                حذف
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
